export interface NewsItem {
  id: number
  title: string
  description: string
  date: string
  image: string
  cover_image?: string
  zh?: {
    title: string
    summary: string
    content?: string
    author?: string
  }
  create_datetime?: string
  prev_news?: {
    id: number | null
    has_prev: boolean
  }
  next_news?: {
    id: number | null
    has_next: boolean
  }
}

export interface NewsCategory {
  id: number
  name: string
}

export interface NewsCategoryListResponse {
  data: NewsCategory[]
  page: number
  size: number
  total: number
}

export interface NewsListResponse {
  data: NewsItem[]
  total: number
  page: number
  pageSize: number
}

export interface SdkTotalStatisticsData {
  /** SDK 调用总量，单位：亿，支持小数 */
  total_count: number
}

export interface SdkTotalStatisticsResponse {
  code: string | number
  msg: string
  data: SdkTotalStatisticsData
}

export const useNewsApi = () => {
  const { apiFetch } = useApi()

  /**
   * 获取新闻列表
   * @param page 页码（支持响应式 Ref）
   * @param size 每页数量
   * @param options 可选的 useFetch 配置选项
   */
  const getNewsList = (
    page: Ref<number> | number = 1,
    size: number = 10,
    options?: {
      server?: boolean
      immediate?: boolean
      category_id?: Ref<string | number> | string | number
    },
  ) => {
    // 创建响应式 query 对象，确保 useFetch 能正确响应变化
    const query = computed(() => {
      const p = isRef(page) ? page.value : page
      const cid = options?.category_id
        ? isRef(options.category_id)
          ? options.category_id.value
          : options.category_id
        : undefined

      return {
        page: p,
        size,
        ...(cid && { category_id: cid }),
      }
    })

    return apiFetch<NewsListResponse>('/kdev/company_news/', {
      query,
      // 如果指定了 server 选项，则使用它；否则默认允许 SSR
      ...(options?.server !== undefined && { server: options.server }),
      // 如果指定了 immediate 选项，则使用它；否则默认为 true
      immediate: options?.immediate !== undefined ? options.immediate : true,
    }) as ReturnType<typeof apiFetch<NewsListResponse>>
  }

  /**
   * 获取新闻详情
   * @param id 新闻 ID
   */
  const getNewsDetail = (id: number | string) => {
    if (!id) {
      throw new Error('新闻 ID 不能为空')
    }
    return apiFetch<NewsItem>(`/kdev/company_news/${id}/`)
  }

  /**
   * 获取新闻分类列表
   * @param options 可选的 useFetch 配置选项
   */
  const getNewsCategories = (options?: {
    server?: boolean
    immediate?: boolean
  }) => {
    return apiFetch<NewsCategoryListResponse>('/kdev/news_category/', {
      // 默认允许 SSR；显式传入 server 时使用传入值
      ...(options?.server !== undefined && { server: options.server }),
      immediate: options?.immediate !== undefined ? options.immediate : true,
    }) as ReturnType<typeof apiFetch<NewsCategoryListResponse>>
  }

  /**
   * 获取 SDK 调用量统计
   * @param options 可选的 useFetch 配置选项
   */
  const getSdkTotalStatistics = (options?: {
    server?: boolean
    immediate?: boolean
  }) => {
    return apiFetch<SdkTotalStatisticsResponse>('/kdev/sdk-total-statistics/', {
      ...(options?.server !== undefined && { server: options.server }),
      immediate: options?.immediate !== undefined ? options.immediate : true,
    }) as ReturnType<typeof apiFetch<SdkTotalStatisticsResponse>>
  }

  return {
    getNewsList,
    getNewsDetail,
    getNewsCategories,
    getSdkTotalStatistics,
  }
}
